import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Rating,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import SectionHeading from "../common/SectionHeading";
import { Stagger, StaggerItem } from "../motion/Stagger";
import TiltCard from "../common/TiltCard";
import appsContent, { getAppDescription, getAppName, getAppScreenshots } from "../../data/appsContent";

export default function Products() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box component="section" sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <SectionHeading
          eyebrow={t("productsEyebrow")}
          title={t("productsTitle")}
          subtitle={t("productsSubtitle")}
        />

        <Stagger>
          <Grid container spacing={3}>
            {appsContent.map((app) => {
              const appName = getAppName(app, i18n.language);
              const screenshots = getAppScreenshots(app, i18n.language);
              return (
              <Grid item xs={12} md={6} key={app.key}>
                <StaggerItem sx={{ height: "100%" }}>
                  <TiltCard max={6}>
                    <Card
                      sx={{
                        height: "100%",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-6px)",
                          boxShadow: "0 36px 70px -34px rgba(13,18,40,0.6)",
                          borderColor: "primary.main",
                        },
                      }}
                    >
                      {/* Banner with screenshot, gradient overlay */}
                      <Box
                        sx={{
                          position: "relative",
                          height: 170,
                          overflow: "hidden",
                          background: "linear-gradient(135deg,#1b2550,#0e1428)",
                        }}
                      >
                        <Box
                          component="img"
                          src={screenshots[0]}
                          alt={appName}
                          loading="lazy"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "top",
                            opacity: 0.85,
                            transition: "transform .6s ease",
                            ".MuiCard-root:hover &": { transform: "scale(1.08)" },
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(180deg, rgba(7,11,24,0) 30%, rgba(7,11,24,0.85) 100%)",
                          }}
                        />
                        <Box
                          component="img"
                          src={app.iconUrl}
                          alt=""
                          sx={{
                            position: "absolute",
                            left: 20,
                            bottom: -22,
                            width: 64,
                            height: 64,
                            borderRadius: 3,
                            border: "3px solid",
                            borderColor: "background.paper",
                            boxShadow: "0 10px 24px -8px rgba(0,0,0,0.5)",
                          }}
                        />
                      </Box>

                      <CardContent sx={{ pt: 4 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Typography variant="h6">{appName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {app.developer}
                            </Typography>
                          </Box>
                          <Chip
                            icon={<DownloadRoundedIcon sx={{ fontSize: 16 }} />}
                            label={app.installs}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                          <Rating
                            value={parseFloat(app.rating)}
                            precision={0.1}
                            size="small"
                            readOnly
                          />
                          <Typography variant="body2" color="text.secondary">
                            {app.rating}
                          </Typography>
                        </Stack>

                        <Typography sx={{ mt: 1.5, mb: 2.5 }} color="text.secondary">
                          {getAppDescription(app, i18n.language)}
                        </Typography>

                        <Button
                          variant="text"
                          endIcon={<ArrowForwardRoundedIcon />}
                          onClick={() => navigate("/apps")}
                          sx={{ px: 0, fontWeight: 700 }}
                        >
                          {t("productsLearnMore")}
                        </Button>
                      </CardContent>
                    </Card>
                  </TiltCard>
                </StaggerItem>
              </Grid>
              );
            })}
          </Grid>
        </Stagger>

        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Button
            size="large"
            variant="outlined"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => navigate("/apps")}
            sx={{
              borderColor: "divider",
              color: "text.primary",
              "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
            }}
          >
            {t("productsViewAll")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
